/* ═══════════════════════════════════════
   AirLog Code Helpers
   Tiny syntax highlighter (no library)
   ═══════════════════════════════════════ */

   import { SYNTAX_TOKEN_COLORS } from './constants'

   /* ── Per-language keyword sets ──
    *
    * Lightweight — we don't try to be a real lexer. We just tag a
    * handful of language-specific keywords so the code "feels" colored
    * the way you'd see in Xcode/VSCode without shipping prism.js.
    */
   const KEYWORDS = {
     javascript: ['const','let','var','function','return','if','else','for','while','do','switch','case','break','continue','class','extends','new','this','super','import','export','from','default','async','await','try','catch','finally','throw','typeof','instanceof','in','of','null','undefined','true','false'],
     typescript: ['const','let','var','function','return','if','else','for','while','do','switch','case','break','continue','class','extends','new','this','super','import','export','from','default','async','await','try','catch','finally','throw','typeof','instanceof','in','of','null','undefined','true','false','interface','type','enum','implements','public','private','protected','readonly','as','any','string','number','boolean','void','never','unknown'],
     python: ['def','class','if','elif','else','for','while','return','import','from','as','pass','break','continue','try','except','finally','raise','with','lambda','yield','async','await','True','False','None','and','or','not','is','in','global','nonlocal','self'],
     swift: ['func','var','let','if','else','for','in','while','return','class','struct','enum','protocol','extension','import','public','private','internal','fileprivate','open','static','self','Self','init','deinit','guard','defer','do','catch','throw','throws','try','as','is','nil','true','false','switch','case','break','continue','where','typealias','associatedtype'],
     java: ['public','private','protected','class','interface','extends','implements','static','final','void','int','long','double','float','boolean','char','byte','short','String','return','if','else','for','while','do','switch','case','break','continue','new','this','super','null','true','false','import','package','try','catch','finally','throw','throws'],
     c: ['int','char','float','double','void','short','long','signed','unsigned','const','static','extern','return','if','else','for','while','do','switch','case','break','continue','struct','typedef','sizeof','enum','union','NULL'],
     cpp: ['int','char','float','double','void','short','long','signed','unsigned','const','static','extern','return','if','else','for','while','do','switch','case','break','continue','struct','typedef','sizeof','enum','union','NULL','class','public','private','protected','virtual','template','typename','namespace','using','new','delete','this','nullptr','true','false','auto'],
     csharp: ['public','private','protected','internal','class','interface','struct','enum','namespace','using','static','readonly','const','void','int','string','bool','double','float','return','if','else','for','foreach','while','do','switch','case','break','continue','new','this','base','null','true','false','try','catch','finally','throw','async','await'],
     go: ['package','import','func','var','const','type','struct','interface','return','if','else','for','range','switch','case','break','continue','defer','go','select','chan','map','nil','true','false'],
     rust: ['fn','let','mut','const','static','if','else','for','while','loop','return','match','struct','enum','impl','trait','use','mod','pub','self','Self','as','in','where','async','await','move','ref','true','false'],
     php: ['function','return','if','else','elseif','for','foreach','while','do','switch','case','break','continue','class','interface','extends','implements','public','private','protected','static','final','abstract','new','self','parent','this','null','true','false','use','namespace','echo','print','require','include'],
     ruby: ['def','end','class','module','if','elsif','else','unless','while','until','for','in','do','return','yield','break','next','begin','rescue','ensure','raise','self','nil','true','false','require','include','attr_reader','attr_writer','attr_accessor'],
     kotlin: ['fun','val','var','if','else','when','for','while','do','return','class','interface','object','companion','data','sealed','open','final','public','private','protected','internal','override','abstract','import','package','null','true','false','this','super','as','is','in'],
     bash: ['if','then','else','elif','fi','for','while','do','done','case','esac','function','return','exit','echo','export','source','local','true','false'],
     sql: ['SELECT','FROM','WHERE','INSERT','INTO','VALUES','UPDATE','SET','DELETE','CREATE','TABLE','ALTER','DROP','INDEX','VIEW','JOIN','LEFT','RIGHT','INNER','OUTER','ON','GROUP','BY','ORDER','HAVING','LIMIT','OFFSET','AS','AND','OR','NOT','IN','EXISTS','NULL','TRUE','FALSE','PRIMARY','KEY','FOREIGN','REFERENCES'],
     matlab: ['function','return','if','else','elseif','end','for','while','switch','case','otherwise','break','continue','try','catch','classdef','properties','methods','events','enumeration'],
     r: ['function','if','else','for','while','repeat','break','next','return','TRUE','FALSE','NULL','NA','Inf','NaN','library','require'],
     html: [],  // handled separately
     css: [],   // handled separately
     json: [],  // handled separately
     markdown: [],
     plaintext: [],
   }
   
   /* ── Escape HTML so user code never breaks layout ── */
   const escapeHtml = (s) => s
     .replace(/&/g, '&amp;')
     .replace(/</g, '&lt;')
     .replace(/>/g, '&gt;')
   
   /* ── Build the regex that matches comments, strings, numbers, and
    *    keywords for a given language. Order matters: comments and
    *    strings must be detected BEFORE keywords so we don't color
    *    keywords that live inside a string. ── */
   function buildTokenRegex(langId) {
     const keywords = KEYWORDS[langId] || []
     const kwGroup = keywords.length
       ? `\\b(?:${keywords.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`
       : null
   
     /* For SQL we want case-insensitive keyword matching. */
     const flags = langId === 'sql' ? 'gi' : 'g'
   
     /* Python and Ruby and Bash use # for comments.
      * Most C-family use // and /* * /.
      * SQL uses -- */
     let commentPattern
     if (['python', 'ruby', 'bash', 'r', 'matlab'].includes(langId)) {
       commentPattern = `#[^\\n]*`
     } else if (langId === 'sql') {
       commentPattern = `--[^\\n]*`
     } else {
       commentPattern = `//[^\\n]*|/\\*[\\s\\S]*?\\*/`
     }
   
     const stringPattern = `"(?:[^"\\\\\\n]|\\\\.)*"|'(?:[^'\\\\\\n]|\\\\.)*'|\`(?:[^\`\\\\]|\\\\.)*\``
     const numberPattern = `\\b\\d+(?:\\.\\d+)?\\b`
   
     const parts = [commentPattern, stringPattern, numberPattern]
     if (kwGroup) parts.push(kwGroup)
   
     return { regex: new RegExp(`(${parts.join(')|(')})`, flags), hasKw: !!kwGroup }
   }
   
   /* ── highlight: turn `code` (string) into HTML with <span> classes
    *    around tokens. Cheap, no library, runs on the main thread. ── */
   export function highlight(code, langId) {
     if (!code) return ''
     if (!langId || langId === 'plaintext' || langId === 'markdown' || langId === 'json') {
       // Plain — but JSON gets simple string/number coloring
       if (langId === 'json') return highlightJson(code)
       return escapeHtml(code)
     }
     if (langId === 'html') return highlightHtml(code)
     if (langId === 'css') return highlightCss(code)
   
     const { regex, hasKw } = buildTokenRegex(langId)
     const out = []
     let lastEnd = 0
     let match
     while ((match = regex.exec(code)) !== null) {
       const start = match.index
       if (start > lastEnd) out.push(escapeHtml(code.slice(lastEnd, start)))
       const [, comment, string, number, keyword] = match
       if (comment != null) out.push(`<span class="syn-c">${escapeHtml(comment)}</span>`)
       else if (string != null) out.push(`<span class="syn-s">${escapeHtml(string)}</span>`)
       else if (number != null) out.push(`<span class="syn-n">${escapeHtml(number)}</span>`)
       else if (hasKw && keyword != null) out.push(`<span class="syn-k">${escapeHtml(keyword)}</span>`)
       lastEnd = regex.lastIndex
     }
     if (lastEnd < code.length) out.push(escapeHtml(code.slice(lastEnd)))
     return out.join('')
   }
   
   /* ── JSON: simple string/number/keyword (true/false/null) coloring ── */
   function highlightJson(code) {
     const safe = escapeHtml(code)
     return safe
       .replace(/("(?:[^"\\]|\\.)*")(\s*:)?/g, (_, str, colon) => {
         const cls = colon ? 'syn-k' : 'syn-s'
         return `<span class="${cls}">${str}</span>${colon || ''}`
       })
       .replace(/\b(true|false|null)\b/g, '<span class="syn-k">$1</span>')
       .replace(/\b(\d+(?:\.\d+)?)\b/g, '<span class="syn-n">$1</span>')
   }
   
   /* ── HTML: tag names + attribute names ── */
   function highlightHtml(code) {
     const safe = escapeHtml(code)
     return safe.replace(/(&lt;\/?)([a-zA-Z][\w-]*)|(\s)([a-zA-Z-]+)(=)/g, (m, open, tag, sp, attr, eq) => {
       if (tag) return `${open}<span class="syn-k">${tag}</span>`
       if (attr) return `${sp}<span class="syn-n">${attr}</span>${eq}`
       return m
     })
   }
   
   /* ── CSS: selectors + property names + values ── */
   function highlightCss(code) {
     const safe = escapeHtml(code)
     return safe
       .replace(/(\/\*[\s\S]*?\*\/)/g, '<span class="syn-c">$1</span>')
       .replace(/([a-zA-Z-]+)(\s*:)/g, '<span class="syn-n">$1</span>$2')
       .replace(/(#[0-9a-fA-F]{3,8})\b/g, '<span class="syn-s">$1</span>')
       .replace(/\b(\d+(?:\.\d+)?(?:px|em|rem|%|vh|vw|s|ms)?)\b/g, '<span class="syn-n">$1</span>')
   }
   
   /* CODE_FONT — the font stack for ALL code (pad, preview, gutter,
    * keyboard toolbar). Inter first, then SF Pro, then system. No mono.
    * Import this anywhere code text is rendered so it stays consistent. */
   export const CODE_FONT = `"Inter", -apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif`
   
   /* Token color CSS — injected by App once at startup so all code views
    * pick it up consistently. Exported so any component can stringify it.
    * BLUE-ONLY palette, Inter font, no italic, normal weight. */
   export const SYNTAX_CSS = `
     .syn-k { color: ${SYNTAX_TOKEN_COLORS.keyword}; font-weight: 400; }
     .syn-s { color: ${SYNTAX_TOKEN_COLORS.string}; font-weight: 400; }
     .syn-n { color: ${SYNTAX_TOKEN_COLORS.number}; font-weight: 400; }
     .syn-c { color: ${SYNTAX_TOKEN_COLORS.comment}; font-weight: 400; }
     .syn-p { color: ${SYNTAX_TOKEN_COLORS.punct}; font-weight: 400; }
   `